import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const THEME_STORAGE_KEY = 'co-pilot-theme';

export const ThemeContext = createContext(null);

const isTheme = (value) => value === 'light' || value === 'dark';

function readStoredTheme() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    // Private browsing / blocked storage: fall back to the system theme.
    return null;
  }
}

function systemTheme() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
}

/**
 * Resolves the theme the same way the pre-paint boot script in index.html
 * does, so the first React render always agrees with what is already on
 * <html> and nothing flashes.
 */
export function resolveInitialTheme() {
  return readStoredTheme() ?? systemTheme();
}

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(resolveInitialTheme);

  // Whether the user has made an explicit choice. Until they do, the app
  // keeps following the OS; after they do, the OS is ignored.
  const [isExplicit, setIsExplicit] = useState(() => readStoredTheme() !== null);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow the OS only while the user has not overridden it.
  useEffect(() => {
    if (isExplicit) {
      return undefined;
    }

    let media;
    try {
      media = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return undefined;
    }

    const handleChange = (event) => {
      setThemeState(event.matches ? 'dark' : 'light');
    };

    // addEventListener on MediaQueryList is unsupported in older Safari.
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }

    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, [isExplicit]);

  const setTheme = useCallback((next) => {
    if (!isTheme(next)) {
      return;
    }

    setThemeState(next);
    setIsExplicit(true);

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Persistence is a nicety - the toggle still works for this session.
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next = current === 'dark' ? 'light' : 'dark';
      setIsExplicit(true);

      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // See setTheme.
      }

      return next;
    });
  }, []);

  /** Drop the override and go back to following the OS preference. */
  const followSystemTheme = useCallback(() => {
    try {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } catch {
      // Ignore - state below still reverts for this session.
    }

    setIsExplicit(false);
    setThemeState(systemTheme());
  }, []);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === 'dark',
      followsSystem: !isExplicit,
      setTheme,
      toggleTheme,
      followSystemTheme,
    }),
    [theme, isExplicit, setTheme, toggleTheme, followSystemTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export default ThemeContext;
